import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    Button,
    Alert,
    Skeleton,
    TableSortLabel,
} from '@mui/material';
import { Search, Category, Download } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { WarehouseAPI } from '../services/api';
import { CardingPackagingUsageEntry } from '../types';
import { ImageThumb, ImageLightbox, LightboxSection } from '../components/carding/CardingImageComponents';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';

const MotionTableRow = motion(TableRow);

type SortKey = 'packagingSku' | 'usageCount';

const CardingPackagingUsageTab: React.FC = () => {
    const [entries, setEntries] = useState<CardingPackagingUsageEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lightboxSections, setLightboxSections] = useState<LightboxSection[] | null>(null);

    // Client-side sort (default most-used-first) and page. Both run over the FULL filtered
    // list below, then the page is sliced out — so sorting reorders every row, not just the page.
    const [sortKey, setSortKey] = useState<SortKey>('usageCount');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(0);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
        else { setSortKey(key); setSortDir(key === 'usageCount' ? 'desc' : 'asc'); }
    };

    // Stored in the URL ("p" prefix to avoid colliding with other Carding sub-tabs) so a
    // refresh restores the same search instead of clearing it.
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get('pq') || '';
    const setSearchQuery = (v: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (v) next.set('pq', v); else next.delete('pq');
            return next;
        }, { replace: true });
    };

    const fetchUsage = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await WarehouseAPI.getCardingPackagingUsage();
            setEntries(data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to load packaging usage');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchUsage(); }, [fetchUsage]);

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return entries;
        const q = searchQuery.trim().toLowerCase();
        return entries.filter(e =>
            e.packagingSku.toLowerCase().includes(q) ||
            e.usedBySkus.some(u => u.sku.toLowerCase().includes(q))
        );
    }, [entries, searchQuery]);

    // Sort the FULL filtered list, then slice the page below — never sort just the page.
    const sorted = useMemo(() => {
        const dir = sortDir === 'asc' ? 1 : -1;
        const arr = [...filtered];
        arr.sort((a, b) => {
            switch (sortKey) {
                case 'packagingSku':
                    return a.packagingSku.localeCompare(b.packagingSku) * dir;
                case 'usageCount':
                    return (a.usageCount - b.usageCount) * dir;
                default:
                    return 0;
            }
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    // Search is the only filter here — reset to the first page whenever it changes.
    useEffect(() => { setPage(0); }, [searchQuery]);

    const paged = useMemo(
        () => sorted.slice(page * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE),
        [sorted, page]
    );

    const buildPackagingSections = (entry: CardingPackagingUsageEntry): LightboxSection[] => [
        { title: `Packaging — ${entry.packagingSku}`, images: entry.images || [] },
    ];

    const buildSkuSections = (sku: string, images: string[]): LightboxSection[] => [
        { title: sku, images: images || [] },
    ];

    const handleExport = () => {
        if (!entries || entries.length === 0) return;

        const csvRows = [
            ['Packaging SKU', 'Images', 'Used By SKUs'].join(',')
        ];

        entries.forEach(entry => {
            const sku = `"${entry.packagingSku.replace(/"/g, '""')}"`;
            const images = `"${(entry.images || []).join(' | ').replace(/"/g, '""')}"`;
            const usedBy = `"${entry.usedBySkus.map(u => u.sku).join(', ').replace(/"/g, '""')}"`;
            csvRows.push([sku, images, usedBy].join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `packaging-usage-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1, borderRadius: 2 }} />
                ))}
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" action={<Button onClick={fetchUsage}>Retry</Button>} sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search by packaging SKU or SKU…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ minWidth: 280, bgcolor: '#ffffff' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Chip
                    icon={<Category sx={{ fontSize: 16 }} />}
                    label={`${filtered.length} packaging SKUs`}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: '#FAFAF7' }}
                />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleExport}
                    disabled={entries.length === 0}
                    sx={{ ml: 'auto' }}
                >
                    Export
                </Button>
            </Box>

            {filtered.length === 0 ? (
                <Alert severity="info">No packaging usage data yet — visit the Carding tab to sync SKUs from Plytix first.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow sx={{ '& .MuiTableCell-head': { py: 1.5 } }}>
                                <TableCell sx={{ width: 56 }}>Image</TableCell>
                                <TableCell sortDirection={sortKey === 'packagingSku' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'packagingSku'} direction={sortKey === 'packagingSku' ? sortDir : 'asc'} onClick={() => handleSort('packagingSku')}>Packaging SKU</TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sortDirection={sortKey === 'usageCount' ? sortDir : false}>
                                    <TableSortLabel active={sortKey === 'usageCount'} direction={sortKey === 'usageCount' ? sortDir : 'desc'} onClick={() => handleSort('usageCount')}>Used by</TableSortLabel>
                                </TableCell>
                                <TableCell>SKUs</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <AnimatePresence initial={false}>
                                {paged.map((entry, idx) => {
                                    const visibleSkus = entry.usedBySkus.slice(0, 4);
                                    return (
                                        <MotionTableRow
                                            key={entry.packagingSku}
                                            hover
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.22, delay: Math.min(idx, 12) * 0.02, ease: 'easeOut' }}
                                        >
                                            <TableCell>
                                                <ImageThumb
                                                    images={entry.images}
                                                    label={entry.packagingSku}
                                                    onClick={() => setLightboxSections(buildPackagingSections(entry))}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{entry.packagingSku}</TableCell>
                                            <TableCell align="right">
                                                <Chip label={entry.usageCount} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(220, 88, 41, 0.08)', color: '#6d28d9' }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {visibleSkus.map(u => (
                                                        <Chip
                                                            key={u.sku}
                                                            label={u.sku}
                                                            size="small"
                                                            onClick={() => setLightboxSections(buildSkuSections(u.sku, u.images))}
                                                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer' }}
                                                        />
                                                    ))}
                                                    {entry.usedBySkus.length > 4 && (
                                                        <Chip
                                                            label={`+${entry.usedBySkus.length - 4} more (Export to see all)`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', borderStyle: 'dashed' }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </MotionTableRow>
                                    );
                                })}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePaginationBar page={page} total={filtered.length} rowsPerPage={DEFAULT_PAGE_SIZE} onPageChange={setPage} />

            <ImageLightbox
                open={!!lightboxSections}
                onClose={() => setLightboxSections(null)}
                sections={lightboxSections || []}
            />
        </Box>
    );
};

export default CardingPackagingUsageTab;
